import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ArrowLeft,
  Ban,
  Columns3,
  Pencil,
  Plus,
  Star,
  Trash2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/lib/errors';
import {
  useAddTemplateStage,
  useAllRejectionReasons,
  useCreatePipelineTemplate,
  useCreateRejectionReason,
  useDeletePipelineTemplate,
  useDeleteTemplateStage,
  usePipelineTemplate,
  usePipelineTemplates,
  useReorderTemplateStages,
  useUpdatePipelineTemplate,
  useUpdateRejectionReason,
  useUpdateTemplateStage,
} from '@/hooks/use-recruitment';
import type { PipelineTemplate, RejectionReason } from '@/types/recruitment';
import {
  PipelineStagesEditor,
  type AddStageInput,
  type UpdateStageInput,
} from './pipeline-stages-editor';

interface GlobalVacancySettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type View = { mode: 'list' } | { mode: 'editor'; templateId: string };

export function GlobalVacancySettingsDialog({
  open,
  onOpenChange,
}: GlobalVacancySettingsDialogProps) {
  const [view, setView] = useState<View>({ mode: 'list' });

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setView({ mode: 'list' });
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="flex max-h-[85vh] w-[680px] max-w-[680px] flex-col gap-0 overflow-hidden rounded-xl p-0 [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">Vacancy Settings</DialogTitle>
        <DialogDescription className="sr-only">
          Manage global recruitment pipeline templates.
        </DialogDescription>

        <div className="flex shrink-0 items-center justify-between border-b border-border px-6 py-[18px]">
          <div className="flex items-center gap-2.5">
            <svg
              className="h-[18px] w-[18px] shrink-0 text-muted-foreground"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="text-base font-bold tracking-tight">Vacancy Settings</span>
          </div>
          <button
            type="button"
            className="flex h-[30px] w-[30px] cursor-pointer items-center justify-center rounded-lg border border-border bg-background text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground"
            onClick={() => handleOpenChange(false)}
            aria-label="Close"
          >
            <X className="h-[15px] w-[15px]" />
          </button>
        </div>

        <Tabs defaultValue="templates" className="flex min-h-0 flex-1 flex-col">
          <TabsList
            data-slot="vacancy-settings-tabs"
            className="h-auto w-full shrink-0 justify-start gap-0 rounded-none border-b border-border bg-muted/40 p-0 px-6 [&>button]:bg-transparent [&>button[data-state=active]]:bg-transparent [&>button[data-state=active]]:shadow-none"
          >
            <TabsTrigger
              value="templates"
              className="gap-1.5 rounded-none border-b-2 border-transparent px-4 py-3 text-[13px] font-medium text-muted-foreground transition-colors duration-150 hover:text-foreground data-[state=active]:border-b-2 data-[state=active]:border-hygge data-[state=active]:font-semibold data-[state=active]:text-foreground"
            >
              <Columns3 className="h-3.5 w-3.5" />
              Pipeline Templates
            </TabsTrigger>
            <TabsTrigger
              value="rejection-reasons"
              className="gap-1.5 rounded-none border-b-2 border-transparent px-4 py-3 text-[13px] font-medium text-muted-foreground transition-colors duration-150 hover:text-foreground data-[state=active]:border-b-2 data-[state=active]:border-hygge data-[state=active]:font-semibold data-[state=active]:text-foreground"
            >
              <Ban className="h-3.5 w-3.5" />
              Rejection Reasons
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto">
            <TabsContent value="templates" className="mt-0 p-5">
              {view.mode === 'list' ? (
                <TemplatesList onOpenEditor={(id) => setView({ mode: 'editor', templateId: id })} />
              ) : (
                <TemplateEditor
                  templateId={view.templateId}
                  onBack={() => setView({ mode: 'list' })}
                />
              )}
            </TabsContent>
            <TabsContent value="rejection-reasons" className="mt-0 p-5">
              <RejectionReasonsSettings />
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

// ── Templates List ──────────────────────────────────────────────

interface TemplatesListProps {
  onOpenEditor: (templateId: string) => void;
}

function TemplatesList({ onOpenEditor }: TemplatesListProps) {
  const { data: templates, isLoading } = usePipelineTemplates();
  const createMutation = useCreatePipelineTemplate();
  const deleteMutation = useDeletePipelineTemplate();
  const [deleteTarget, setDeleteTarget] = useState<PipelineTemplate | null>(null);

  const handleCreate = async () => {
    const name = nextDraftName(templates ?? []);
    const created = await createMutation.mutateAsync({ name });
    onOpenEditor(created.id);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
        Loading templates...
      </div>
    );
  }

  if (!templates || templates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-md border border-dashed border-border py-12 text-center">
        <Columns3 className="h-8 w-8 text-muted-foreground" />
        <div>
          <p className="text-sm font-medium text-foreground">No pipeline templates yet</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Create a template to standardize hiring stages across vacancies.
          </p>
        </div>
        <Button
          size="sm"
          className="mt-2 cursor-pointer bg-cta text-cta-foreground hover:bg-cta/90"
          onClick={handleCreate}
          disabled={createMutation.isPending}
        >
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Create Template
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-[13px] font-semibold text-foreground">Pipeline Templates</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Reusable hiring pipelines that get applied to new vacancies.
          </p>
        </div>
        <Button
          size="sm"
          className="h-9 cursor-pointer bg-cta text-cta-foreground hover:bg-cta/90"
          onClick={handleCreate}
          disabled={createMutation.isPending}
        >
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Create Template
        </Button>
      </div>

      <div className="flex flex-col gap-1">
        {templates.map((t) => {
          const stageCount = t.stages.length;
          return (
            <div
              key={t.id}
              className="group flex items-center gap-3 rounded-md border border-border px-3 py-2.5 transition-colors hover:border-hygge/30 hover:bg-hygge/5"
            >
              <button
                type="button"
                className="flex flex-1 cursor-pointer items-center gap-3 text-left"
                onClick={() => onOpenEditor(t.id)}
              >
                <Columns3 className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span
                      className="truncate text-[13px] font-medium text-foreground"
                      title={t.name}
                    >
                      {t.name}
                    </span>
                    {t.isDefault && (
                      <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-800 dark:bg-amber-950 dark:text-amber-200">
                        <Star className="h-2.5 w-2.5" />
                        Default
                      </span>
                    )}
                  </div>
                  {t.description && (
                    <p
                      className="mt-0.5 truncate text-xs text-muted-foreground"
                      title={t.description}
                    >
                      {t.description}
                    </p>
                  )}
                </div>
                <span className="shrink-0 rounded bg-muted px-2 py-0.5 text-[10px] font-semibold tabular-nums text-muted-foreground">
                  {stageCount} stages
                </span>
              </button>
              <div className="flex gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                <button
                  type="button"
                  className="flex h-[26px] w-[26px] cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                  onClick={() => onOpenEditor(t.id)}
                  aria-label="Edit template"
                  title="Edit template"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  className={cn(
                    'flex h-[26px] w-[26px] items-center justify-center rounded-md text-muted-foreground',
                    t.isDefault
                      ? 'cursor-not-allowed opacity-40'
                      : 'cursor-pointer hover:bg-muted hover:text-destructive',
                  )}
                  onClick={() => {
                    if (!t.isDefault) setDeleteTarget(t);
                  }}
                  disabled={t.isDefault}
                  aria-label="Delete template"
                  title={t.isDefault ? 'Cannot delete the default template' : 'Delete template'}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(v) => {
          if (!v) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="wrap-anywhere">
              Delete template "{deleteTarget?.name}"?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the template. Vacancies that previously copied stages from
              this template are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="cursor-pointer">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="cursor-pointer bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (!deleteTarget) return;
                deleteMutation.mutate(deleteTarget.id);
                setDeleteTarget(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function nextDraftName(templates: PipelineTemplate[]) {
  const base = 'New Template';
  const names = new Set(templates.map((t) => t.name.toLowerCase()));
  if (!names.has(base.toLowerCase())) return base;
  let i = 2;
  while (names.has(`${base} ${i}`.toLowerCase())) i++;
  return `${base} ${i}`;
}

// ── Template Editor ────────────────────────────────────────────

interface TemplateEditorProps {
  templateId: string;
  onBack: () => void;
}

function TemplateEditor({ templateId, onBack }: TemplateEditorProps) {
  const { data: template, isLoading } = usePipelineTemplate(templateId);
  const updateMutation = useUpdatePipelineTemplate();
  const addStageMutation = useAddTemplateStage(templateId);
  const updateStageMutation = useUpdateTemplateStage(templateId);
  const deleteStageMutation = useDeleteTemplateStage(templateId);
  const reorderStageMutation = useReorderTemplateStages(templateId);

  const [nameDraft, setNameDraft] = useState<string | null>(null);
  const [descDraft, setDescDraft] = useState<string | null>(null);

  if (isLoading || !template) {
    return (
      <div>
        <BackButton onBack={onBack} />
        <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
          Loading template...
        </div>
      </div>
    );
  }

  const nameValue = nameDraft ?? template.name;
  const descValue = descDraft ?? template.description ?? '';

  const commitName = () => {
    const trimmed = nameValue.trim();
    if (trimmed && trimmed !== template.name) {
      updateMutation.mutate({ id: templateId, name: trimmed });
    }
    setNameDraft(null);
  };

  const commitDesc = () => {
    const trimmed = descValue.trim();
    const current = template.description ?? '';
    if (trimmed !== current) {
      updateMutation.mutate({ id: templateId, description: trimmed || null });
    }
    setDescDraft(null);
  };

  const toggleDefault = (next: boolean) => {
    updateMutation.mutate({ id: templateId, isDefault: next });
  };

  const handleAdd = (input: AddStageInput) => addStageMutation.mutate(input);
  const handleUpdate = (stageId: string, input: UpdateStageInput) =>
    updateStageMutation.mutate({ stageId, ...input });
  const handleDelete = (stageId: string) => deleteStageMutation.mutate(stageId);
  const handleReorder = (positions: { id: string; position: number }[]) =>
    reorderStageMutation.mutate({ stages: positions });

  return (
    <div className="space-y-5">
      <BackButton onBack={onBack} />

      <div className="space-y-3 rounded-md border border-border p-4">
        <div className="space-y-1.5">
          <Label className="text-[13px] font-semibold">Template Name</Label>
          <Input
            value={nameValue}
            maxLength={100}
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={commitName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              if (e.key === 'Escape') setNameDraft(null);
            }}
            className="h-9"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-[13px] font-semibold">Description</Label>
          <Textarea
            value={descValue}
            placeholder="Optional — short note about when to use this template"
            onChange={(e) => setDescDraft(e.target.value)}
            onBlur={commitDesc}
            rows={2}
            className="resize-none text-sm"
          />
        </div>

        <div className="flex items-start gap-3 pt-1">
          <Switch
            id={`default-${templateId}`}
            checked={template.isDefault}
            onCheckedChange={toggleDefault}
            disabled={template.isDefault || updateMutation.isPending}
          />
          <div className="-mt-0.5">
            <Label
              htmlFor={`default-${templateId}`}
              className="cursor-pointer text-[13px] font-medium"
            >
              Default template
            </Label>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Applied to newly created vacancies. Only one template can be default.
            </p>
          </div>
        </div>
      </div>

      <PipelineStagesEditor
        stages={template.stages}
        onAdd={handleAdd}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        onReorder={handleReorder}
        pending={{ add: addStageMutation.isPending, update: updateStageMutation.isPending }}
      />
    </div>
  );
}

function BackButton({ onBack }: { onBack: () => void }) {
  return (
    <button
      type="button"
      onClick={onBack}
      className="inline-flex cursor-pointer items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-3.5 w-3.5" />
      Back to templates
    </button>
  );
}

// ── Rejection Reasons ──────────────────────────────────────────

function RejectionReasonsSettings() {
  const { data: reasons, isLoading } = useAllRejectionReasons();
  const createMutation = useCreateRejectionReason();
  const [newLabel, setNewLabel] = useState('');

  const handleCreate = () => {
    const label = newLabel.trim();
    if (!label) return;
    createMutation.mutate(
      { label, sortOrder: (reasons?.length ?? 0) * 10 },
      {
        onSuccess: () => setNewLabel(''),
        onError: (err) =>
          toast.error(extractErrorMessage(err, 'Could not add reason — it may already exist')),
      },
    );
  };

  return (
    <div>
      <div className="mb-4">
        <h3 className="text-[13px] font-semibold text-foreground">Rejection Reasons</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Predefined reasons recruiters pick when rejecting a candidate. Disable a reason to
          hide it from the dropdown without losing past analytics.
        </p>
      </div>

      <div className="mb-3 flex gap-2">
        <Input
          value={newLabel}
          maxLength={100}
          placeholder="Add a rejection reason..."
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleCreate();
          }}
          className="h-9"
        />
        <Button
          size="sm"
          className="h-9 shrink-0 cursor-pointer bg-cta text-cta-foreground hover:bg-cta/90"
          onClick={handleCreate}
          disabled={!newLabel.trim() || createMutation.isPending}
        >
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Add
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
          Loading reasons...
        </div>
      ) : !reasons || reasons.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border py-10 text-center">
          <Ban className="h-7 w-7 text-muted-foreground" />
          <p className="text-sm font-medium text-foreground">No rejection reasons yet</p>
          <p className="text-xs text-muted-foreground">
            Add reasons above so recruiters reject candidates with consistent data.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-1">
          {reasons.map((reason) => (
            <RejectionReasonRow key={reason.id} reason={reason} />
          ))}
        </div>
      )}
    </div>
  );
}

function RejectionReasonRow({ reason }: { reason: RejectionReason }) {
  const updateMutation = useUpdateRejectionReason({ suppressGlobalToast: true });
  const [labelDraft, setLabelDraft] = useState<string | null>(null);

  const labelValue = labelDraft ?? reason.label;

  const commitLabel = () => {
    const trimmed = labelValue.trim();
    if (trimmed && trimmed !== reason.label) {
      updateMutation.mutate(
        { id: reason.id, label: trimmed },
        {
          onError: (err) =>
            toast.error(
              extractErrorMessage(err, 'Could not rename — the reason may already exist'),
            ),
        },
      );
    }
    setLabelDraft(null);
  };

  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-md border border-border px-3 py-2 transition-colors',
        !reason.isActive && 'opacity-60',
      )}
    >
      <Input
        value={labelValue}
        maxLength={100}
        onChange={(e) => setLabelDraft(e.target.value)}
        onBlur={commitLabel}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'Escape') setLabelDraft(null);
        }}
        className="h-8 flex-1 border-transparent bg-transparent px-1.5 text-[13px] focus-visible:border-border focus-visible:bg-background"
      />
      <div className="flex shrink-0 items-center gap-2">
        <span className="text-[11px] text-muted-foreground">
          {reason.isActive ? 'Active' : 'Disabled'}
        </span>
        <Switch
          checked={reason.isActive}
          onCheckedChange={(next) =>
            updateMutation.mutate(
              { id: reason.id, isActive: next },
              {
                onError: (err) =>
                  toast.error(extractErrorMessage(err, 'Could not update reason status')),
              },
            )
          }
          disabled={updateMutation.isPending}
        />
      </div>
    </div>
  );
}
